import { apiPath } from "../config";

export type ApiErrorBody = {
  error: string;
  code?: string;
  detail?: string;
  file?: string;
};

export class ApiError extends Error {
  status: number;
  body?: ApiErrorBody;
  constructor(status: number, message: string, body?: ApiErrorBody) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

export type RequestOptions = {
  method?: string;
  body?: unknown;
  headers?: Record<string, string>;
  credentials?: RequestCredentials;
};

declare global {
  interface Window {
    __CC_API_DEBUG__?: boolean;
  }
}

/** Toggle verbose API error logging in the browser console (admin/site setting). */
export function setApiDebugEnabled(enabled: boolean): void {
  if(typeof window === "undefined") return;
  window.__CC_API_DEBUG__ = !!enabled;
}

function isApiDebugEnabled(): boolean {
  return typeof window !== "undefined" && !!window.__CC_API_DEBUG__;
}

function logApiFailure(method: string, path: string, status: number, data: unknown): void {
  if(!isApiDebugEnabled()) return;
  console.error("[CC API]", method, path, status, data);
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const {method = "GET", body, headers = {}, credentials = "include"} = options;
  const init: RequestInit = {
    method,
    credentials,
    headers: {
      Accept: "application/json",
      ...headers
    }
  };
  if(body !== undefined) {
    init.headers = {...init.headers as Record<string, string>, "Content-Type": "application/json"};
    init.body = JSON.stringify(body);
  }
  const res = await fetch(apiPath(path), init);
  const text = await res.text();
  let data: unknown = null;
  if(text) {
    try {
      data = JSON.parse(text);
    } catch {
      data = text;
    }
  }
  if(!res.ok) {
    logApiFailure(method, path, res.status, data);
    const errBody = typeof data === "object" && data !== null ? data as ApiErrorBody : undefined;
    throw new ApiError(res.status, errBody?.error || res.statusText, errBody);
  }
  return data as T;
}

export async function apiFormRequest<T>(path: string, form: FormData, method = "POST"): Promise<T> {
  const res = await fetch(apiPath(path), {method, credentials: "include", body: form});
  const text = await res.text();
  let data: unknown = null;
  if(text) {
    try { data = JSON.parse(text); } catch { data = text; }
  }
  if(!res.ok) {
    logApiFailure(method, path, res.status, data);
    const errBody = typeof data === "object" && data !== null ? data as ApiErrorBody : undefined;
    throw new ApiError(res.status, errBody?.error || res.statusText, errBody);
  }
  return data as T;
}

export type HealthResponse = {
  ok: boolean;
  service: string;
  version: string;
  time: string;
};

export function getHealth(): Promise<HealthResponse> {
  return apiRequest<HealthResponse>("/health");
}
